import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {GlobalState} from '../../../store/store.model';
import {catchError, EMPTY, filter, map, of, switchMap, tap, withLatestFrom} from 'rxjs';
import {ProfileApiService} from '../services/profile-api.service';
import {
  deleteProfile,
  deleteProfileError,
  deleteProfileSuccess,
  editProfile,
  editProfileError,
  editProfileSuccess,
  loadLocations,
  loadLocationsError,
  loadLocationsSuccess,
  loadProfile,
  loadProfileError,
  loadProfileSuccess,
  logout
} from './profile.actions';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {selectGuest} from '../../flow/store/flow.selectors';
import * as LocationActions from '../../location/store/location.actions';

@Injectable()
export class ProfileEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store<GlobalState>);
  private readonly _profileApiService = inject(ProfileApiService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _transloco = inject(TranslocoService);

  private _loadProfile = createEffect(() =>
    this._actions$.pipe(
      ofType(loadProfile),
      switchMap(() =>
        this._profileApiService.getProfile().pipe(
          map(profile => loadProfileSuccess({profile})),
          catchError(() => of(loadProfileError()))
        )
      )
    )
  );

  private _loadLocations = createEffect(() => {
    return this._actions$.pipe(
      ofType(loadLocations),
      withLatestFrom(this._store.select(selectGuest)),
      switchMap(([{}, guestMode]) => {
        if (!guestMode) {
          return this._profileApiService.getLocations().pipe(
            map(response =>
              LocationActions.loadLocationsSuccess({
                locations: response?.data,
                status: 'success',
              })
            ),
            catchError(() => of(LocationActions.loadLocationsError()))
          )
        } else {
          return EMPTY;
        }
      })
    );
  });

  private _editProfile = createEffect(() =>
    this._actions$.pipe(
      ofType(editProfile),
      switchMap(({data}) =>
        this._profileApiService.editProfile(data).pipe(
          map((response) => editProfileSuccess({response})),
          catchError(() => of(editProfileError()))
        )
      )
    )
  );

  private _editProfileSuccess = createEffect(() => {
    return this._actions$.pipe(
      ofType(editProfileSuccess),
      tap(({response}) => {
        if (response.status !== 'error') {
          this._snackBar.open(
            this._transloco.translate('profile.success_update'),
            undefined,
            {duration: 3000, panelClass: 'snackbar'}
          );
          this._store.dispatch(loadProfile());
        } else {
          this._snackBar.open(
            this._transloco.translate('profile.error_update', {reason: response.message}),
            undefined,
            {duration: 3000, panelClass: 'snackbar'}
          );
        }
      })
    );
  }, {dispatch: false});


  private _deleteProfile = createEffect(() => {
    return this._actions$.pipe(
      ofType(deleteProfile),
      switchMap(() =>
        this._profileApiService.deleteProfile().pipe(
          map((response) => deleteProfileSuccess({response})),
          catchError(() => of(deleteProfileError()))
        )
      )
    );
  });

  private _deleteProfileSuccess = createEffect(() =>
    this._actions$.pipe(
      ofType(deleteProfileSuccess),
      filter(({response}) => response?.status === 'success'),
      map(() => logout())
    )
  );

  private _deleteProfileError = createEffect(() =>
    this._actions$.pipe(
      ofType(deleteProfileError),
      tap(() => this._snackBar.open(
        this._transloco.translate('profile.delete_account_error'),
        undefined,
        {duration: 3000, panelClass: 'snackbar'}
      ))
    ));

}
